import { ErrorCode } from './error-codes'

import type { RestResponse, ValidationError } from './rest-response'

const HTTP_STATUS_TO_REASON: Record<number, string> = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  408: 'REQUEST_TIMEOUT',
  409: 'CONFLICT',
  422: 'UNPROCESSABLE_ENTITY',
  429: 'TOO_MANY_REQUESTS',
  500: 'INTERNAL_ERROR',
  501: 'NOT_IMPLEMENTED',
  502: 'BAD_GATEWAY',
  503: 'SERVICE_UNAVAILABLE',
  504: 'GATEWAY_TIMEOUT',
}

const ERROR_TO_HTTP_STATUS: Record<ErrorCode, number> = {
  // ── General ─────────────────────────────────────────
  [ErrorCode.VALIDATION_ERROR]: 400,
  [ErrorCode.NOT_FOUND]: 404,
  [ErrorCode.UNAUTHORIZED]: 401,
  [ErrorCode.FORBIDDEN]: 403,
  [ErrorCode.CONFLICT]: 409,
  [ErrorCode.TOO_MANY_REQUESTS]: 429,
  [ErrorCode.REQUEST_TIMEOUT]: 408,
  [ErrorCode.INTERNAL_ERROR]: 500,
  [ErrorCode.SERVICE_UNAVAILABLE]: 503,
  [ErrorCode.NOT_IMPLEMENTED]: 501,

  // ── HTTP Client ──────────────────────────────────────
  [ErrorCode.HTTP_BAD_REQUEST]: 400,
  [ErrorCode.HTTP_UNAUTHORIZED]: 401,
  [ErrorCode.HTTP_FORBIDDEN]: 403,
  [ErrorCode.HTTP_NOT_FOUND]: 404,
  [ErrorCode.HTTP_TIMEOUT]: 408,
  [ErrorCode.HTTP_INTERNAL_ERROR]: 500,
  [ErrorCode.NETWORK_ERROR]: 502,
  [ErrorCode.HTTP_TOO_MANY_REQUESTS]: 429,
  [ErrorCode.HTTP_BAD_GATEWAY]: 502,
  [ErrorCode.HTTP_SERVICE_UNAVAILABLE]: 503,

  // ── Auth ─────────────────────────────────────────────
  [ErrorCode.AUTH_INVALID_CREDENTIALS]: 401,
  [ErrorCode.AUTH_TOKEN_EXPIRED]: 401,
  [ErrorCode.AUTH_TOKEN_INVALID]: 401,

  // ── Database ─────────────────────────────────────────
  [ErrorCode.DB_RECORD_NOT_FOUND]: 404,
  [ErrorCode.DB_DUPLICATE_ENTRY]: 409,
  [ErrorCode.DB_TRANSACTION_FAILED]: 500,

  // ── External Service ─────────────────────────────────
  [ErrorCode.EXTERNAL_SERVICE_ERROR]: 502,
  [ErrorCode.PAYMENT_GATEWAY_ERROR]: 502,
  [ErrorCode.STORAGE_ERROR]: 502,

  // ── Resilience ───────────────────────────────────────
  [ErrorCode.CIRCUIT_BREAKER_OPEN]: 503,
  [ErrorCode.RESILIENCE_EXHAUSTED]: 503,
  [ErrorCode.QUOTATION_INVALID_STATUS_TRANSITION]: 400,

}

export class AppError extends Error {
  public readonly httpCode?: number
  public readonly code: ErrorCode
  public readonly errors?: ValidationError[] | null
  public readonly data?: unknown

  constructor(code: ErrorCode, message: string, options?: { httpCode?: number; errors?: ValidationError[] | null; data?: unknown }) {
    super(message)
    this.code = code
    this.httpCode = options?.httpCode
    this.data = options?.data
    this.errors = options?.errors
    this.name = 'AppError'
    // ponytail: V8-only, guarded for non-V8 envs
    ;(Error as { captureStackTrace?: (t: object, c: unknown) => void }).captureStackTrace?.(this, this.constructor)
  }

  /** Derive HTTP status from code when httpCode is not explicitly set. */
  toHttpStatus(): number {
    if (this.httpCode) return this.httpCode
    return ERROR_TO_HTTP_STATUS[this.code] ?? 500
  }

  getHttpResponse(): RestResponse {
    const httpStatus = this.toHttpStatus()
    return {
      success: false,
      message: this.message,
      code: this.code,
      reason: HTTP_STATUS_TO_REASON[httpStatus] ?? 'INTERNAL_ERROR',
      errors: this.errors,
      data: (this.data as unknown) ?? null,
    }
  }

  getErrorResponse() {
    return {
      code: this.code,
      message: this.message,
      reason: HTTP_STATUS_TO_REASON[this.toHttpStatus()] ?? 'INTERNAL_ERROR',
      errors: this.errors,
      data: this.data,
    }
  }
}
